import * as React from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "../../../containers/ApiContainer";
import { useSnackbar } from "../../../containers/SnackbarContainer";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { formatDistance } from "date-fns";
import { AutocompleteAircraft } from "../../Settings/HangarSettings/AutocompleteAircraft";
import { QuarantineService } from "../../../services/QuarantineService";
import { Aircraft } from "../../../types";
import { looksLikeRealTail } from "../../../utils/tailNumber";

// PostgREST result type
type PendingTenantRow = {
  id: string;
  fbo_id: string;
  tail_number: string;
  operator_name: string | null;
  aircraft_type_hint: string | null;
  source: string;
  created_at: string;
  resolved_at: string | null;
  notes: string | null;
  fbo: { name: string } | null;
};

// Flattened type for presentation
export type PendingTenant = {
  id: string;
  fbo_id: string;
  fbo_name: string;
  tail_number: string;
  operator_name: string;
  aircraft_type_hint: string;
  source: string;
  created_at: string;
  resolved_at: string | null;
  notes: string | null;
};

export const QuarantineTab: React.FC = () => {
  const { t } = useTranslation();
  const { postgrest } = useApi();
  const snackbar = useSnackbar();
  const [pendingTenants, setPendingTenants] = React.useState<PendingTenant[]>(
    []
  );
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const [resolveDialogOpen, setResolveDialogOpen] = React.useState(false);
  const [resolving, setResolving] = React.useState(false);

  // Inline mapping state
  const [processingId, setProcessingId] = React.useState<string | null>(null);
  const [registerTailMap, setRegisterTailMap] = React.useState<
    Map<string, boolean>
  >(new Map());

  const shouldRegisterTail = (tenantId: string) => {
    // If user has explicitly toggled, use their choice
    const explicit = registerTailMap.get(tenantId);
    if (explicit !== undefined) {
      return explicit;
    }
    // Otherwise, intelligently detect based on tail number format
    const tenant = pendingTenants.find((t) => t.id === tenantId);
    return tenant ? looksLikeRealTail(tenant.tail_number) : true;
  };

  const toggleRegisterTail = (tenantId: string) => {
    setRegisterTailMap((prev) => {
      const next = new Map(prev);
      next.set(tenantId, !shouldRegisterTail(tenantId));
      return next;
    });
  };

  const fetchPendingTenants = React.useCallback(async () => {
    setLoading(true);
    try {
      const result = await postgrest
        .from("flightbridge_pending_tenants")
        .select(
          "id, fbo_id, tail_number, operator_name, aircraft_type_hint, source, created_at, resolved_at, notes, fbo:fbo_id(name)"
        )
        .is("resolved_at", null)
        .order("created_at", { ascending: false });

      if (result.error) {
        setError(result.error.message);
        return;
      }

      if (result.data) {
        const mapped = result.data.map((row: PendingTenantRow) => ({
          id: row.id,
          fbo_id: row.fbo_id,
          fbo_name: row.fbo?.name || t('screens.superAdmin.quarantineTab.defaults.unknownFbo'),
          tail_number: row.tail_number,
          operator_name: row.operator_name || t('screens.superAdmin.quarantineTab.defaults.unknownOperator'),
          aircraft_type_hint: row.aircraft_type_hint || t('screens.superAdmin.quarantineTab.defaults.unknownTypeHint'),
          source: row.source,
          created_at: row.created_at,
          resolved_at: row.resolved_at,
          notes: row.notes,
        }));
        setPendingTenants(mapped);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [postgrest]);

  React.useEffect(() => {
    fetchPendingTenants();
  }, [fetchPendingTenants]);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds(new Set(pendingTenants.map((t) => t.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleResolve = async () => {
    if (selectedIds.size === 0) return;

    setResolving(true);
    try {
      await QuarantineService.deletePendingTenants(
        postgrest,
        Array.from(selectedIds)
      );

      // Refresh the list
      await fetchPendingTenants();
      setSelectedIds(new Set());
      setResolveDialogOpen(false);
      snackbar.notify({
        text: t('screens.superAdmin.quarantineTab.snackbar.removed', { count: selectedIds.size }),
        severity: "success",
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setResolving(false);
    }
  };

  // Inline mapping handler
  const handleMapAircraft = async (tenant: PendingTenant, aircraft: Aircraft) => {
    const registerTail = shouldRegisterTail(tenant.id);
    setProcessingId(tenant.id);

    try {
      const serviceTenant = {
        id: tenant.id,
        fbo_id: tenant.fbo_id,
        tail_number: tenant.tail_number,
        operator_name: tenant.operator_name,
        aircraft_type_hint: tenant.aircraft_type_hint,
      };

      const result = registerTail
        ? await QuarantineService.resolveWithFaaMapping(
            postgrest,
            serviceTenant,
            aircraft
          )
        : await QuarantineService.resolveWithDirectTenant(
            postgrest,
            serviceTenant,
            aircraft
          );

      snackbar.notify({ text: result.message, severity: "success" });
      await fetchPendingTenants();
    } catch (err) {
      snackbar.notify({
        text: err instanceof Error ? err.message : t('screens.superAdmin.quarantineTab.snackbar.mapFailed'),
        severity: "error",
      });
    } finally {
      setProcessingId(null);
    }
  };

  const allSelected =
    pendingTenants.length > 0 && selectedIds.size === pendingTenants.length;
  const someSelected =
    selectedIds.size > 0 && selectedIds.size < pendingTenants.length;

  return (
    <Stack direction="column" spacing={3} sx={{ p: 2, height: "100%" }}>
      {/* Header */}
      <Box>
        <Typography variant="h6" fontWeight="bold">
          {t('screens.superAdmin.quarantineTab.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('screens.superAdmin.quarantineTab.description')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {pendingTenants.length === 0 && !loading ? (
        <Alert severity="success">
          {t('screens.superAdmin.quarantineTab.successMessage')}
        </Alert>
      ) : (
        <Paper
          elevation={1}
          sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
        >
          {/* Toolbar */}
          <Box
            sx={{
              p: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {selectedIds.size > 0
                ? t('screens.superAdmin.quarantineTab.selectionCount', { count: selectedIds.size, total: pendingTenants.length })
                : t('screens.superAdmin.quarantineTab.pendingCount', { count: pendingTenants.length })}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                startIcon={
                  loading ? <CircularProgress size={16} /> : <RefreshIcon />
                }
                onClick={fetchPendingTenants}
                disabled={loading}
              >
                {t('screens.superAdmin.quarantineTab.buttons.refresh')}
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<DeleteSweepIcon />}
                disabled={selectedIds.size === 0 || loading}
                onClick={() => setResolveDialogOpen(true)}
              >
                {t('screens.superAdmin.quarantineTab.buttons.removeAndRetry', { count: selectedIds.size })}
              </Button>
            </Stack>
          </Box>

          {/* Table */}
          <TableContainer sx={{ flexGrow: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    padding="checkbox"
                    sx={{ backgroundColor: "background.paper" }}
                  >
                    <Checkbox
                      indeterminate={someSelected}
                      checked={allSelected}
                      onChange={handleSelectAll}
                      disabled={loading || pendingTenants.length === 0}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "background.paper",
                      fontWeight: "bold",
                    }}
                  >
                    {t('screens.superAdmin.quarantineTab.tableHeaders.tailNumber')}
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "background.paper",
                      fontWeight: "bold",
                    }}
                  >
                    {t('screens.superAdmin.quarantineTab.tableHeaders.fbo')}
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "background.paper",
                      fontWeight: "bold",
                    }}
                  >
                    {t('screens.superAdmin.quarantineTab.tableHeaders.typeHint')}
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "background.paper",
                      fontWeight: "bold",
                      minWidth: 250,
                    }}
                  >
                    {t('screens.superAdmin.quarantineTab.tableHeaders.mapToAircraft')}
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "background.paper",
                      fontWeight: "bold",
                      textAlign: "center",
                    }}
                  >
                    <Tooltip title={t('screens.superAdmin.quarantineTab.tooltips.registerExplainer')}>
                      <span>{t('screens.superAdmin.quarantineTab.tableHeaders.register')}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "background.paper",
                      fontWeight: "bold",
                    }}
                  >
                    {t('screens.superAdmin.quarantineTab.tableHeaders.source')}
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "background.paper",
                      fontWeight: "bold",
                    }}
                  >
                    {t('screens.superAdmin.quarantineTab.tableHeaders.created')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : pendingTenants.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('screens.superAdmin.quarantineTab.table.noPendingTenants')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  pendingTenants.map((tenant) => {
                    const isProcessing = processingId === tenant.id;
                    return (
                      <TableRow
                        key={tenant.id}
                        hover
                        selected={selectedIds.has(tenant.id)}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedIds.has(tenant.id)}
                            onChange={() => handleSelectRow(tenant.id)}
                            disabled={isProcessing}
                          />
                        </TableCell>
                        <TableCell>
                          <Link
                            href={`https://www.flightradar24.com/data/aircraft/${tenant.tail_number}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                          >
                            <Typography variant="body2" fontWeight="medium">
                              {tenant.tail_number}
                            </Typography>
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {tenant.fbo_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontStyle: "italic", color: "text.secondary" }}
                          >
                            {tenant.aircraft_type_hint}
                          </Typography>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {isProcessing ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <CircularProgress size={20} />
                              <Typography variant="body2" color="text.secondary">
                                {t('screens.superAdmin.quarantineTab.table.mapping')}
                              </Typography>
                            </Box>
                          ) : (
                            <AutocompleteAircraft
                              label={t('screens.superAdmin.quarantineTab.table.selectAircraft')}
                              onSelect={(aircraft) =>
                                handleMapAircraft(tenant, aircraft)
                              }
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Tooltip
                              title={
                                shouldRegisterTail(tenant.id)
                                  ? t('screens.superAdmin.quarantineTab.tooltips.willAddToFaa')
                                  : t('screens.superAdmin.quarantineTab.tooltips.quotePlaceholder')
                              }
                              placement="left"
                            >
                              <Checkbox
                                checked={shouldRegisterTail(tenant.id)}
                                onChange={() => toggleRegisterTail(tenant.id)}
                                disabled={isProcessing}
                                size="small"
                              />
                            </Tooltip>
                            {/* Fixed-width space for icon alignment */}
                            <Box sx={{ width: 20, display: "flex", justifyContent: "center" }}>
                              {!looksLikeRealTail(tenant.tail_number) && (
                                <Tooltip
                                  title={t('screens.superAdmin.quarantineTab.tooltips.placeholderWarning')}
                                  placement="right"
                                >
                                  <WarningAmberIcon
                                    sx={{
                                      fontSize: 16,
                                      color: "warning.main",
                                      opacity: 0.7,
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={tenant.source}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDistance(
                              new Date(tenant.created_at),
                              new Date(),
                              { addSuffix: true }
                            )}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Resolution Dialog */}
      <Dialog
        open={resolveDialogOpen}
        onClose={() => setResolveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('screens.superAdmin.quarantineTab.dialog.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('screens.superAdmin.quarantineTab.dialog.description', { count: selectedIds.size })}
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>{t('screens.superAdmin.quarantineTab.dialog.autoRetryTitle')}</strong> {t('screens.superAdmin.quarantineTab.dialog.autoRetryBody')}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            {t('screens.superAdmin.quarantineTab.dialog.retryNote')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialogOpen(false)}>{t('screens.superAdmin.quarantineTab.dialog.cancelButton')}</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleResolve}
            disabled={resolving}
            startIcon={
              resolving ? <CircularProgress size={16} /> : <DeleteSweepIcon />
            }
          >
            {resolving ? t('screens.superAdmin.quarantineTab.dialog.removing') : t('screens.superAdmin.quarantineTab.dialog.confirmButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
